import React from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight, Sparkles, Loader2 } from 'lucide-react';
import StoryboardSidebar from './StoryboardStep/StoryboardSidebar';
import MomentEditor from './StoryboardStep/MomentEditor';

export default function StoryboardStep({
  chapters,
  searchQuery,
  selectedMoment,
  activeTab,
  isTechnicalMode,
  generatingStoryboard,
  projectId,
  onSearchChange,
  onMomentSelect,
  onTabChange,
  onModeChange,
  onMomentUpdate,
  onSaveInteraction,
  onGenerateStoryboard,
  onPublish,
  onBack,
  onNext,
}) {
  const moment = chapters
    .flatMap((ch) => ch.moments)
    .find((m) => m.id === selectedMoment);

  // If no chapters, show generate button
  if (chapters.length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <div className="bg-zinc-900 rounded-lg p-12 text-center">
          <Sparkles className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h2 className="text-2xl font-bold mb-2">No Storyboard Generated</h2>
          <p className="text-gray-400 mb-6">
            Generate a storyboard from your training content using AI
          </p>
          <button
            onClick={onGenerateStoryboard}
            disabled={generatingStoryboard}
            className="bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-medium flex items-center gap-2 mx-auto transition-colors"
          >
            {generatingStoryboard ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating Storyboard...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Storyboard
              </>
            )}
          </button>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6 pt-6 border-t border-zinc-700">
          <button
            onClick={onBack}
            className="flex items-center text-sm text-gray-300 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 w-full  min-h-[600px]">
      {/* Left Sidebar - Storyboard Navigation */}
      <div className="flex-shrink-0 relative z-999">
        <StoryboardSidebar
          chapters={chapters}
          searchQuery={searchQuery}
          selectedMoment={selectedMoment}
          onSearchChange={onSearchChange}
          onMomentSelect={onMomentSelect}
          isTechnicalMode={isTechnicalMode}
        />
      </div>

      {/* Right Panel - Moment Details Editor */}
      <div className="flex-1 flex flex-col min-w-0">
        {moment ? (
          <MomentEditor
            chapters={chapters}
            searchQuery={searchQuery}
            selectedMoment={selectedMoment}
            onSearchChange={onSearchChange}
            onMomentSelect={onMomentSelect}
            isTechnicalMode={isTechnicalMode}
            moment={moment}
            activeTab={activeTab}
            onTabChange={onTabChange}
            onModeChange={onModeChange}
            onMomentUpdate={onMomentUpdate}
            onSaveInteraction={onSaveInteraction}
            onPublish={onPublish}
          />
        ) : (
          <div className="bg-zinc-900 rounded-lg p-12 text-center">
            <p className="text-gray-400">
              Select a moment from the sidebar to edit
            </p>
          </div>
        )}

        {/* Navigation Buttons */}
        {/* <div className="flex justify-between mt-6 pt-6 border-t border-zinc-700 flex-shrink-0">
          <button
            onClick={onBack}
            className="flex items-center text-sm text-gray-300 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          <button
            onClick={onNext}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded text-sm font-medium flex items-center transition-colors"
          >
            Continue
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div> */}
      </div>
    </div>
  );
}

StoryboardStep.propTypes = {
  chapters: PropTypes.arrayOf(PropTypes.object).isRequired,
  searchQuery: PropTypes.string.isRequired,
  selectedMoment: PropTypes.string,
  activeTab: PropTypes.string.isRequired,
  isTechnicalMode: PropTypes.bool.isRequired,
  generatingStoryboard: PropTypes.bool.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onMomentSelect: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onMomentUpdate: PropTypes.func.isRequired,
  onGenerateStoryboard: PropTypes.func.isRequired,
  onPublish: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

StoryboardStep.defaultProps = {
  selectedMoment: null,
};
